import { atom, useRecoilState } from "recoil";
import { useCallback, useEffect, useRef } from "react";
import { localStorageDB } from "@/Data/LocalStorage";
import {
    generateHomePicture as generateHomePictureAPI,
    getHomePictureTask,
    listHomePictures,
    HomePicture,
} from "@/Data/World";

export type PollingStatus = 'idle' | 'polling' | 'completed' | 'failed';

type PersonaRoomPicState = {
    roomPics:      Record<string, string>;
    pollingStatus: Record<string, PollingStatus>;
    taskIds:       Record<string, string>;
    error:         Record<string, string | null>;
};

const STORAGE_KEY = 'personatest:roompic';
const POLL_INTERVAL = 3000;

const makeKey = (addressid: string, typeid: string) => `${addressid}:${typeid}`;

const loadFromStorage = (): PersonaRoomPicState => {
    try {
        const stored = localStorageDB.getItem(STORAGE_KEY);
        if (stored) {
            const parsed = JSON.parse(stored);
            return {
                roomPics:      parsed.roomPics      ?? {},
                pollingStatus: parsed.pollingStatus ?? {},
                taskIds:       parsed.taskIds       ?? {},
                error:         {},
            };
        }
    } catch (e) { console.error('Failed to load PersonaRoomPic from storage:', e); }
    return { roomPics: {}, pollingStatus: {}, taskIds: {}, error: {} };
};

const saveToStorage = (state: PersonaRoomPicState): void => {
    try {
        localStorageDB.setItem(STORAGE_KEY, JSON.stringify({
            roomPics:      state.roomPics,
            pollingStatus: state.pollingStatus,
            taskIds:       state.taskIds,
        }));
    } catch (e) { console.error('Failed to save PersonaRoomPic to storage:', e); }
};

const personaRoomPicState = atom<PersonaRoomPicState>({
    key: 'personaRoomPicState',
    default: loadFromStorage(),
});

export const usePersonaRoomPic = (addressid: string, typeid: string) => {
    const [state, setState] = useRecoilState(personaRoomPicState);
    const pollingRef = useRef<ReturnType<typeof setInterval> | null>(null);

    const key           = makeKey(addressid, typeid);
    const roomPic       = state.roomPics[key] ?? null;
    const pollingStatus = state.pollingStatus[key] ?? 'idle';
    const taskId        = state.taskIds[key] ?? null;
    const error         = state.error[key] ?? null;

    const fetchRoomPics = useCallback(async (): Promise<HomePicture[]> => {
        const pictures = await listHomePictures(addressid);
        if (pictures.length > 0) {
            setState(prev => {
                const updated = {
                    ...prev,
                    roomPics:      { ...prev.roomPics, [key]: pictures[0].image },
                    pollingStatus: { ...prev.pollingStatus, [key]: 'idle' as PollingStatus },
                };
                saveToStorage(updated);
                return updated;
            });
        }
        return pictures;
    }, [addressid, key, setState]);

    const startGeneration = useCallback(async (roomImage: string): Promise<string | null> => {
        setState(prev => {
            const updated = {
                ...prev,
                pollingStatus: { ...prev.pollingStatus, [key]: 'polling' as PollingStatus },
                error:         { ...prev.error, [key]: null },
            };
            saveToStorage(updated);
            return updated;
        });

        const newTaskId = await generateHomePictureAPI({ room_image: roomImage, addressid });

        if (!newTaskId) {
            setState(prev => {
                const updated = {
                    ...prev,
                    pollingStatus: { ...prev.pollingStatus, [key]: 'failed' as PollingStatus },
                    error:         { ...prev.error, [key]: 'Failed to start generation' },
                };
                saveToStorage(updated);
                return updated;
            });
            return null;
        }

        setState(prev => {
            const updated = { ...prev, taskIds: { ...prev.taskIds, [key]: newTaskId } };
            saveToStorage(updated);
            return updated;
        });

        return newTaskId;
    }, [addressid, key, setState]);

    const cancelGeneration = useCallback(() => {
        if (pollingRef.current) {
            clearInterval(pollingRef.current);
            pollingRef.current = null;
        }
        setState(prev => {
            const { [key]: _, ...restTaskIds } = prev.taskIds;
            const updated = {
                ...prev,
                pollingStatus: { ...prev.pollingStatus, [key]: 'idle' as PollingStatus },
                taskIds:       restTaskIds,
                error:         { ...prev.error, [key]: null },
            };
            saveToStorage(updated);
            return updated;
        });
    }, [key, setState]);

    const clearError = useCallback(() => {
        setState(prev => ({ ...prev, error: { ...prev.error, [key]: null } }));
    }, [key, setState]);

    // Auto-polling: taskId가 있으면 polling 시작
    useEffect(() => {
        if (!taskId) { return; }

        const poll = async () => {
            const task = await getHomePictureTask(taskId);

            if (!task) {
                if (pollingRef.current) { clearInterval(pollingRef.current); pollingRef.current = null; }
                setState(prev => {
                    const { [key]: _, ...restTaskIds } = prev.taskIds;
                    const updated = {
                        ...prev,
                        pollingStatus: { ...prev.pollingStatus, [key]: 'failed' as PollingStatus },
                        taskIds:       restTaskIds,
                        error:         { ...prev.error, [key]: 'Failed to get task status' },
                    };
                    saveToStorage(updated);
                    return updated;
                });
                return;
            }

            if (task.status === 'completed' && task.image) {
                if (pollingRef.current) { clearInterval(pollingRef.current); pollingRef.current = null; }
                setState(prev => {
                    const { [key]: _, ...restTaskIds } = prev.taskIds;
                    const updated = {
                        ...prev,
                        roomPics:      { ...prev.roomPics, [key]: task.image! },
                        pollingStatus: { ...prev.pollingStatus, [key]: 'completed' as PollingStatus },
                        taskIds:       restTaskIds,
                        error:         { ...prev.error, [key]: null },
                    };
                    saveToStorage(updated);
                    return updated;
                });
                return;
            }

            if (task.status === 'failed') {
                if (pollingRef.current) { clearInterval(pollingRef.current); pollingRef.current = null; }
                setState(prev => {
                    const { [key]: _, ...restTaskIds } = prev.taskIds;
                    const updated = {
                        ...prev,
                        pollingStatus: { ...prev.pollingStatus, [key]: 'failed' as PollingStatus },
                        taskIds:       restTaskIds,
                        error:         { ...prev.error, [key]: 'Room picture generation failed' },
                    };
                    saveToStorage(updated);
                    return updated;
                });
            }
        };

        poll();
        pollingRef.current = setInterval(poll, POLL_INTERVAL);

        return () => {
            if (pollingRef.current) {
                clearInterval(pollingRef.current);
                pollingRef.current = null;
            }
        };
    }, [key, taskId, setState]);

    return {
        typeid,
        roomPic,
        pollingStatus,
        error,
        fetchRoomPics,
        startGeneration,
        cancelGeneration,
        clearError,
    };
};
